import { To } from 'react-router-dom';
import { v4 } from 'uuid';

import { create } from '../create-store/createStore';

export type BreadcrumbItem = {
  id: string;
  to?: To;
  isAccessibleFromSideBar?: boolean;
  navigateFn?: () => void;
  label: string;
  themeId?: string;
  assetTag?: string;
};

export type HandleLocationChangeProps = {
  to?: To;
  crumb?: {
    isSidebar?: boolean;
    isAccessibleFromSideBar?: boolean;
    label: string;
    themeId?: string;
    assetTag?: string;
  };
  navigateFn?: () => void;
};

type BreadcrumbsStore = {
  breadcrumbs?: BreadcrumbItem[];
  addBreadCrumbItem: ({
    to,
    label,
  }: {
    to?: To;
    label: string;
    navigateFn?: () => void;
  }) => void;
  removeFromBreadcrumbs: (id: string, shouldReInitialize?: boolean) => void;
  reInitializeWith: (item: BreadcrumbItem) => void;
  handleLocationChange: ({
    to,
    crumb,
    navigateFn,
  }: HandleLocationChangeProps) => void;
};

export const useBreadcrumbs = create<BreadcrumbsStore>((set, get) => ({
  breadcrumbs: undefined,
  addBreadCrumbItem(item) {
    const { breadcrumbs = [] } = get();
    const breadcrumb = {
      ...item,
      id: v4(),
    };
    set({
      breadcrumbs: [...breadcrumbs, breadcrumb],
    });
  },
  removeFromBreadcrumbs(breadcrumbId, shouldReInitialize) {
    const { breadcrumbs = [], reInitializeWith } = get();
    const itemIndex = breadcrumbs.findIndex(b => b.id === breadcrumbId);

    if (itemIndex !== -1) {
      if (shouldReInitialize) {
        reInitializeWith(breadcrumbs[itemIndex]);
      } else {
        const newBreadcrumbs = [...breadcrumbs.slice(0, itemIndex + 1)];
        set({
          breadcrumbs: newBreadcrumbs,
        });
      }
    }
  },
  reInitializeWith(item) {
    set({
      breadcrumbs: [item],
    });
  },
  handleLocationChange({ to, crumb, navigateFn }) {
    const isAccessibleFromSideBar = crumb?.isAccessibleFromSideBar;

    const { addBreadCrumbItem, reInitializeWith } = get();
    if (!crumb) {
      return;
    }

    const item = { ...crumb, id: v4(), to, navigateFn };
    if (crumb.isSidebar || isAccessibleFromSideBar) {
      return reInitializeWith(item);
    }

    addBreadCrumbItem(item);
  },
}));
